import React from "react";
import gql from "graphql-tag";

import { TextWithAbbreviations } from "@jasperlabs/jux-next";
import {
	Config as NumberFormatConfig,
	formatCurrencyFor,
	M_DASH,
} from "@jasperlabs/number-format";

import { FormattedCurrencyTextMoneyFieldsFragment } from "./FormattedCurrencyText.generated";

const DEFAULT_CONFIG: NumberFormatConfig = {
	sign: "-",
};

function makeCurrencyFormatter(
	currency: FormattedCurrencyTextMoneyFieldsFragment["currency"],
	config: NumberFormatConfig = {},
) {
	return formatCurrencyFor({
		...DEFAULT_CONFIG,
		currencySymbol: `${currency.abbreviation} `,
		decimalSeparator: currency.decimalSeparator,
		thousandsSeparator: currency.thousandsSeparator,
		invalidSymbol: M_DASH,
		infinitySymbol: M_DASH,
		...config,
	});
}

export function formatMoney(
	money: Omit<FormattedCurrencyTextMoneyFieldsFragment, "__typename">,
	config?: NumberFormatConfig,
) {
	const { currency } = money;

	const format = makeCurrencyFormatter(currency, config);
	const formattedMoney = format(money.value);

	if (formattedMoney === M_DASH || !currency.abbreviation) {
		return formattedMoney;
	}

	return `${formattedMoney}`;
}

type FormattedCurrencyTextProps = {
	value: number | string;
	currency: FormattedCurrencyTextMoneyFieldsFragment["currency"];
	exchangedFrom?: FormattedCurrencyTextMoneyFieldsFragment["currency"];
	config?: NumberFormatConfig;
};

function FormattedCurrencyText({
	value,
	currency,
	exchangedFrom,
	config,
}: FormattedCurrencyTextProps) {
	const formatCurrency = makeCurrencyFormatter(currency, config);
	const formattedValue = formatCurrency(value);

	if (formattedValue === M_DASH || !currency.abbreviation) {
		return <>{formattedValue}</>;
	}

	if (exchangedFrom?.abbreviation) {
		const formattedAbbreviation = `[[${currency.abbreviation}/${exchangedFrom.abbreviation}]]`;

		return (
			<TextWithAbbreviations as="span">
				{`${formattedAbbreviation} ${formattedValue}`}
			</TextWithAbbreviations>
		);
	}

	return (
		<TextWithAbbreviations as="span">
			{`${formattedValue}`}
		</TextWithAbbreviations>
	);
}

FormattedCurrencyText.fragment = gql`
	fragment FormattedCurrencyTextCurrencyFields on Currency {
		id
		abbreviation
		symbol
		decimalSeparator
		thousandsSeparator
	}

	fragment FormattedCurrencyTextMoneyFields on Money {
		value
		currency {
			...FormattedCurrencyTextCurrencyFields
		}
	}
`;

export default FormattedCurrencyText;
